// ─────────────────────────────────────────────
// KLOTHIQ — Expense Entry Modal
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input, Select } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState, useEffect } from 'react';
import { X, IndianRupee, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useExpenseCategories, useRecordExpenseV2 } from '@/hooks/useAccounts';
import { useCashAccounts } from '@/hooks/useCash';
import { parseRpcError, toISODate } from '@/utils/cashbookCalc';

interface ExpenseEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultCategoryId?: string | null;
}

export default function ExpenseEntryModal({
  isOpen,
  onClose,
  defaultCategoryId,
}: ExpenseEntryModalProps) {
  const { data: categories, isLoading: loadingCategories } = useExpenseCategories();
  const { data: accounts, isLoading: loadingAccounts } = useCashAccounts();
  const recordExpense = useRecordExpenseV2();

  const [categoryId, setCategoryId] = useState(defaultCategoryId || '');
  const [accountId, setAccountId] = useState('');
  const [particular, setParticular] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [method, setMethod] = useState<'cash' | 'upi' | 'bank_transfer' | 'cheque'>('cash');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (defaultCategoryId) {
        setCategoryId(defaultCategoryId);
        const cat = categories?.find((c) => c.id === defaultCategoryId);
        if (cat) setParticular(cat.name);
      }
      setDate(toISODate(new Date()));
    }
  }, [isOpen, defaultCategoryId, categories]);

  useEffect(() => {
    if (isOpen && accounts && accounts.length > 0 && !accountId) {
      const defaultAcc = accounts.find((a) => a.account_type === 'cash') ?? accounts[0];
      if (defaultAcc) setAccountId(defaultAcc.id);
    }
  }, [isOpen, accounts, accountId]);

  if (!isOpen) return null;

  const handleCategoryChange = (newCatId: string) => {
    setCategoryId(newCatId);
    const cat = categories?.find((c) => c.id === newCatId);
    if (cat && (!particular || categories?.some((c) => c.name === particular))) {
      setParticular(cat.name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) {
      toast.error('Please select an account');
      return;
    }
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }
    if (!particular.trim()) {
      toast.error('Please enter a description or select a category');
      return;
    }
    if (!date) {
      toast.error('Date is required');
      return;
    }
    const todayStr = toISODate(new Date());
    if (date > todayStr) {
      toast.error('Date cannot be in the future');
      return;
    }

    setSubmitting(true);
    try {
      await recordExpense.mutateAsync({
        account_id: accountId,
        amount: numAmount,
        particular: particular.trim(),
        date,
        expense_category_id: categoryId || null,
        payment_method: method,
        notes: notes.trim() || null,
      });
      toast.success('Expense recorded successfully');
      onClose();
      // Reset form
      setAmount('');
      setParticular('');
      setNotes('');
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs" data-field-guide-scope>
      <div className="obsidian-panel relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl animate-in fade-in zoom-in-95">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start justify-between gap-3 pr-8">
          <h2 className="text-lg font-semibold text-foreground">Record Expense</h2>
          <UserGuide compact />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Log an operational expense paid from a cash or bank account
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
          {/* Category */}
          <div className="space-y-1.5">
            <label htmlFor="exp-category" className="text-xs font-medium text-foreground">
              Expense Category
            </label>
            <Select
              id="exp-category"
              value={categoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className={inputCls}
              disabled={loadingCategories || submitting}
              helpLabel="Expense Category"
            >
              <option value="">Select category (optional)</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Particular / Description */}
          <div className="space-y-1.5">
            <label htmlFor="exp-particular" className="text-xs font-medium text-foreground">
              Particular / Description <span className="text-destructive">*</span>
            </label>
            <Input
              id="exp-particular"
              type="text"
              placeholder="e.g. Petrol, Courier, Office tea"
              value={particular}
              onChange={(e) => setParticular(e.target.value)}
              className={inputCls}
              disabled={submitting}
              helpLabel="Particular / Description *"
            />
          </div>

          {/* Account */}
          <div className="space-y-1.5">
            <label htmlFor="exp-account" className="text-xs font-medium text-foreground">
              Paid From Account <span className="text-destructive">*</span>
            </label>
            <Select
              id="exp-account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className={inputCls}
              disabled={loadingAccounts || submitting}
              helpLabel="Paid From Account *"
            >
              <option value="">Select account</option>
              {accounts?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — Balance: ₹{Number(a.current_balance).toLocaleString('en-IN')}
                </option>
              ))}
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <label htmlFor="exp-amount" className="text-xs font-medium text-foreground">
              Amount (₹) <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="exp-amount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`${inputCls} pl-8`}
                disabled={submitting}
                helpLabel="Amount (₹) *"
              />
            </div>
          </div>

          {/* Date & Method */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="exp-date" className="text-xs font-medium text-foreground">
                Date <span className="text-destructive">*</span>
              </label>
              <DatePicker
                clearable={false}
                aria-label="Date"
                id="exp-date"
                value={date}
                onChange={(nextDate) => setDate(nextDate ?? '')}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="exp-method" className="text-xs font-medium text-foreground">
                Payment Method
              </label>
              <Select
                id="exp-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as any)}
                className={inputCls}
                disabled={submitting}
                helpLabel="Payment Method"
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <label htmlFor="exp-notes" className="text-xs font-medium text-foreground">
              Notes (Optional)
            </label>
            <Input
              id="exp-notes"
              type="text"
              placeholder="e.g. Receipt #123"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
              disabled={submitting}
              helpLabel="Notes (Optional)"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2.5 pt-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save Expense
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
